import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Form } from "react-bootstrap";
import { useTypeOfWork } from "../../../data/TypeOfWorkHook";
import { useWorkOrders } from "../../../data/WorkOrdersHook";
import useLogger from "../../../util/Logger";
import { useCategories } from "../../../data/CategoriesHook";
import { useSubCategories } from "../../../data/SubCategoriesHook";
import { useAssets } from "../../../data/AssetsHook";
import { useProperty } from "../../../data/PropertyHook";
import { useSla } from "../../../data/SlaHook";
import { isSummer } from "../../../data/Months";

function IssueDescription() {
  const logger = useLogger();
  const navigate = useNavigate();
  const workOrdersRepository = useWorkOrders();
  const typeOfWorkRepository = useTypeOfWork();
  const categoryRepository = useCategories();
  const subCategoryRepository = useSubCategories();
  const assetRepository = useAssets();
  const propertyRepository = useProperty();
  const slaRepository = useSla();

  const [isLoading, setIsLoading] = useState(false);

  const workOrder = workOrdersRepository.getEntry();

  const doneLoading = () => {
    setIsLoading(false);
  };

  const startLoading = () => {
    setIsLoading(true);
  };

  const cascadeUpdateSubCategory = async (revisedWorkOrder) => {
    const subCategories = await subCategoryRepository.updateSubCategories(
      revisedWorkOrder.category
    );
    if (!subCategories) throw new Error(`No subCategories were retrieved`);

    if (subCategories.length === 0) {
      const revised = {
        ...revisedWorkOrder,
        subCategory: "",
        subCategoryName: "",
      };
      workOrdersRepository.setEntry(revised);
    } else {
      const revised = {
        ...revisedWorkOrder,
        subCategory: subCategories[0].id,
        subCategoryName: subCategories[0].name,
      };
      workOrdersRepository.setEntry(revised);
    }

    doneLoading();
  };

  const cascadeUpdateCategory = async (revisedWorkOrder) => {
    const categories = await categoryRepository.getCategories(
      revisedWorkOrder.type
    );
    if (!categories) throw new Error(`No categories were retrieved`);

    // Update SLA
    const property = propertyRepository.selectedProperty();
    const sla = await slaRepository.updateSla(
      property.siteId,
      categories[0].id
    );

    const revised = {
      ...revisedWorkOrder,
      category: categories[0].id,
      categoryName: categories[0].name,
      slahours: isSummer(property.siteName ?? "") ? sla.SummerHrs : sla.WinterHrs,
    };
    workOrdersRepository.setEntry(revised);

    cascadeUpdateSubCategory(revised);
  };

  const updateType = async (e) => {
    try {
      startLoading();
      const current = workOrdersRepository.getEntry();
      const revised = { ...current, type: e.target.value };
      workOrdersRepository.setEntry(revised);
      await cascadeUpdateCategory(revised);
    } catch (error) {
      logger.error(
        "IssueDescription.useEffect",
        `An error occurred while loading extra data: ${error}`
      );
      toast.error(
        "An error occurred while loading the work order supplementary data. Please try again."
      );
      doneLoading();
      navigate("/");
    }
  };

  const updateCategory = async (e) => {
    try {
      startLoading();
      const current = workOrdersRepository.getEntry();

      const newCategoryId = e.target.value;
      const index = e.nativeEvent.target.selectedIndex;
      const categoryName = e.nativeEvent.target[index].text;

      // Update SLA
      const property = propertyRepository.selectedProperty();
      const sla = await slaRepository.updateSla(property.siteId, newCategoryId);
      const revised = {
        ...current,
        category: newCategoryId,
        categoryName,
        slahours: isSummer(property.siteName ?? "") ? sla.SummerHrs : sla.WinterHrs,
      };

      workOrdersRepository.setEntry(revised);
      await cascadeUpdateSubCategory(revised);
    } catch (error) {
      logger.error(
        "IssueDescription.useEffect",
        `An error occurred while loading extra data: ${error}`
      );
      toast.error(
        "An error occurred while loading the work order supplementary data. Please try again."
      );
      doneLoading();
      navigate("/");
    }
  };

  const updateSubCategory = (e) => {
    const current = workOrdersRepository.getEntry();
    const index = e.nativeEvent.target.selectedIndex;
    const subCategoryName = e.nativeEvent.target[index].text;

    const revised = {
      ...current,
      subCategory: e.target.value,
      subCategoryName,
    };
    workOrdersRepository.setEntry(revised);
  };

  const updateAsset = (e) => {
    const current = workOrdersRepository.getEntry();
    const revised = { ...current, asset: e.target.value };
    workOrdersRepository.setEntry(revised);
  };

  const updateFaultDescription = (e) => {
    const current = workOrdersRepository.getEntry();
    const revised = { ...current, faultDescription: e.target.value };
    workOrdersRepository.setEntry(revised);
  };

  const currentTypes = typeOfWorkRepository.getTypeOfWorks();
  const currentCategories = categoryRepository.getCurrentCategories();
  const currentSubCategories = subCategoryRepository.getSubCategories();
  const assets = assetRepository.getAssets();

  return (
    <div className="work-order-entry-section issue-description">
      <h3>Issue Description</h3>
      <Form.Group className="mb-3" controlId="typeOfWorkGroup">
        <Form.Label>Type Of Work</Form.Label>
        <Form.Select value={workOrder.type} onChange={updateType} disabled>
          {currentTypes.map((nextType) => (
            <option key={nextType} value={nextType}>
              {nextType}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3" controlId="categoryGroup">
        <Form.Label>Category</Form.Label>
        <Form.Select
          value={workOrder.category}
          onChange={updateCategory}
          disabled={isLoading}
        >
          {currentCategories.map((nextCategory) => (
            <option key={nextCategory.id} value={nextCategory.id}>
              {nextCategory.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3" controlId="subCategoryGroup">
        <Form.Label>Subcategory</Form.Label>
        <Form.Select
          value={workOrder.subCategory}
          onChange={updateSubCategory}
          disabled={isLoading}
        >
          {currentSubCategories.map((nextSubCategory) => (
            <option key={nextSubCategory.id} value={nextSubCategory.id}>
              {nextSubCategory.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3" controlId="assetGroup">
        <Form.Label>Asset</Form.Label>
        <Form.Select value={workOrder.asset} onChange={updateAsset}>
          {assets.map((nextAsset) => (
            <option key={nextAsset.id} value={nextAsset.id}>
              {nextAsset.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3" controlId="faultDescriptionGroup">
        <Form.Label>Fault Description</Form.Label>
        <Form.Control
          as="textarea"
          required
          rows={6}
          value={workOrder.faultDescription}
          onChange={updateFaultDescription}
        />
      </Form.Group>
    </div>
  );
}

export default IssueDescription;
